import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';
import { FaEnvelope, FaLock } from 'react-icons/fa';
import { MdPhoneAndroid } from 'react-icons/md';
import { authAPI } from '../services/api';
import { trackEvent } from '../utils/facebookPixel';
import { getAttributionForSubmission } from '../utils/attributionTracker';
import PlayerRegistrationConsentSection from '../components/playerSignup/PlayerRegistrationConsentSection';
import SkillReferenceModal from '../components/playerSignup/SkillReferenceModal';
import {
  exactLevelsForRange,
  cantonOptionLabel,
  PLAYTOMIC_BAND_OPTIONS,
  SWISS_PADEL_LEVEL_OPTIONS,
  deriveSkillRangeFromSelections,
  skillRangeHpsLabel,
} from '../constants/playerSignup';

const BRAND = { blue: '#043663', red: '#D52B1E', gray: '#E5E7EB', lightGray: '#f3f4f6' };

const PageWrap = styled.div`
  max-width: 520px;
  margin: 2rem auto;
  padding: 0 1rem;
`;

const Card = styled.div`
  background: #fff;
  border-radius: 16px;
  padding: 1.75rem 1.5rem 2rem;
  box-shadow:
    0 4px 6px -1px rgba(0, 0, 0, 0.06),
    0 10px 24px -4px rgba(4, 54, 99, 0.08);
  border: 1px solid ${BRAND.gray};
  overflow: hidden;
`;

const BrandRow = styled.div`
  text-align: center;
  margin-bottom: 1rem;
  img {
    height: 48px;
    width: auto;
    display: inline-block;
  }
`;

const HeroTitle = styled.h1`
  font-size: 1.35rem;
  font-weight: 700;
  color: #111827;
  margin: 0 0 0.35rem 0;
  text-align: center;
  letter-spacing: -0.02em;
`;

const HeroSubtitle = styled.p`
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0 0 1.25rem;
  line-height: 1.5;
  text-align: center;
`;

const StepperRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 1.75rem;
  padding: 0 0.15rem;
  position: relative;
`;

const StepperTrack = styled.div`
  position: absolute;
  top: 17px;
  left: calc(12.5% + 8px);
  right: calc(12.5% + 8px);
  height: 3px;
  background: ${BRAND.gray};
  border-radius: 999px;
  z-index: 0;
`;

const StepperFill = styled(motion.div)`
  position: absolute;
  top: 17px;
  left: calc(12.5% + 8px);
  height: 3px;
  border-radius: 999px;
  background: linear-gradient(90deg, ${BRAND.blue}, #0a558c);
  z-index: 0;
`;

const StepItem = styled.button`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 1;
  min-width: 0;
  border: none;
  background: transparent;
  padding: 0;
  font: inherit;
  cursor: ${(p) => (p.$clickable ? 'pointer' : 'default')};

  &:focus-visible {
    outline: 2px solid ${BRAND.blue};
    outline-offset: 4px;
    border-radius: 8px;
  }
`;

const StepBubble = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8125rem;
  font-weight: 700;
  transition:
    transform 0.25s cubic-bezier(0.4, 0, 0.2, 1),
    background 0.2s ease,
    color 0.2s ease,
    box-shadow 0.25s ease;
  background: ${(p) =>
    p.$done ? BRAND.blue : p.$active ? BRAND.blue : '#e5e7eb'};
  color: ${(p) => (p.$done || p.$active ? '#fff' : '#9ca3af')};
  box-shadow: ${(p) =>
    p.$active ? '0 4px 12px rgba(4, 54, 99, 0.35)' : 'none'};
  transform: ${(p) => (p.$active ? 'scale(1.06)' : 'scale(1)')};
`;

const StepLabel = styled.span`
  font-size: 0.65rem;
  font-weight: 600;
  color: ${(p) => (p.$active || p.$done ? BRAND.blue : '#9ca3af')};
  margin-top: 0.35rem;
  text-align: center;
  line-height: 1.15;
  max-width: 72px;
  text-transform: uppercase;
  letter-spacing: 0.04em;

  @media (min-width: 480px) {
    font-size: 0.7rem;
    max-width: 88px;
  }
`;

const StepTitle = styled.h2`
  font-size: 1.125rem;
  font-weight: 700;
  color: #111827;
  margin: 0 0 0.35rem 0;
`;

const StepLead = styled.p`
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0 0 1.25rem;
  line-height: 1.5;
`;

const SectionHeading = styled.h3`
  font-size: 1rem;
  font-weight: 700;
  color: #111827;
  margin: 0 0 0.35rem 0;
`;

const SectionLead = styled.p`
  font-size: 0.8125rem;
  color: #6b7280;
  margin: 0 0 1rem;
  line-height: 1.5;
`;

const Label = styled.label`
  display: block;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.375rem;
`;

const FieldWrap = styled.div`
  margin-bottom: 1rem;
`;

const InnerInput = styled.input`
  width: 100%;
  padding: 0.625rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 10px;
  font-size: 0.9375rem;
  box-sizing: border-box;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;
  &:focus {
    outline: none;
    border-color: ${BRAND.blue};
    box-shadow: 0 0 0 3px rgba(4, 54, 99, 0.12);
  }
`;

const InputAdornWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  border: 1px solid #d1d5db;
  border-radius: 10px;
  padding: 0 0.65rem;
  margin-bottom: 1rem;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;
  &:focus-within {
    border-color: ${BRAND.blue};
    box-shadow: 0 0 0 3px rgba(4, 54, 99, 0.12);
  }
  svg {
    flex-shrink: 0;
    color: #9ca3af;
    font-size: 1rem;
  }
  input {
    flex: 1;
    border: none;
    padding: 0.625rem 0.25rem;
    font-size: 0.9375rem;
    min-width: 0;
    background: transparent;
    &:focus {
      outline: none;
    }
  }
`;

const TogglePw = styled.button`
  flex-shrink: 0;
  border: none;
  background: none;
  color: ${BRAND.blue};
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  padding: 0.35rem;
  &:hover {
    opacity: 0.85;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.625rem 0.875rem;
  border: 1px solid #d1d5db;
  border-radius: 10px;
  font-size: 0.9375rem;
  margin-bottom: 1rem;
  background: #fff;
  box-sizing: border-box;
  cursor: pointer;
  &:focus {
    outline: none;
    border-color: ${BRAND.blue};
    box-shadow: 0 0 0 3px rgba(4, 54, 99, 0.12);
  }
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

const BtnPrimary = styled.button`
  flex: 1;
  padding: 0.8rem 1.25rem;
  background: ${BRAND.blue};
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 0.9625rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  transition:
    transform 0.18s ease,
    background 0.2s ease,
    box-shadow 0.2s ease;
  box-shadow: 0 2px 8px rgba(4, 54, 99, 0.2);
  &:hover:not(:disabled) {
    background: #032a4a;
    transform: translateY(-1px);
    box-shadow: 0 4px 14px rgba(4, 54, 99, 0.28);
  }
  &:active:not(:disabled) {
    transform: translateY(0);
  }
  &:disabled {
    opacity: 0.58;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const BtnOutline = styled.button`
  padding: 0.8rem 1rem;
  min-width: 96px;
  background: transparent;
  color: ${BRAND.blue};
  border: 2px solid ${BRAND.blue};
  border-radius: 10px;
  font-size: 0.9375rem;
  font-weight: 600;
  cursor: pointer;
  transition:
    background 0.18s ease,
    color 0.18s ease;
  &:hover:not(:disabled) {
    background: rgba(4, 54, 99, 0.06);
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const BtnRow = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 1.5rem;
  align-items: stretch;
`;

const Hint = styled.p`
  font-size: 0.8125rem;
  color: #6b7280;
  margin: -0.35rem 0 1rem 0;
  line-height: 1.45;
`;

const SummaryBlock = styled.div`
  background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 1rem 1.125rem;
  margin-bottom: 1rem;
`;

const SummaryHeading = styled.div`
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${BRAND.blue};
  margin-bottom: 0.35rem;
`;

const SummaryHeadRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 0.5rem;
  margin-bottom: 0.35rem;
`;

const SummaryBody = styled.p`
  font-size: 0.875rem;
  color: #334155;
  margin: 0;
  line-height: 1.5;
`;

const SummaryEdit = styled.button`
  border: none;
  background: none;
  color: ${BRAND.blue};
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;
  &:hover {
    text-decoration: underline;
  }
`;

const InnerInputBare = styled(InnerInput)`
  margin-bottom: 1rem;
`;

const LoginLink = styled.div`
  text-align: center;
  margin-top: 1.5rem;
  font-size: 0.9375rem;
  color: #6b7280;
  a {
    color: ${BRAND.blue};
    font-weight: 600;
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }
`;

const TurnstileBox = styled.div`
  margin: 1rem 0;
  min-height: 70px;
  display: flex;
  justify-content: center;
`;

const TurnstileAlert = styled.div`
  margin: 0.75rem 0 1rem;
  padding: 0.85rem 1rem;
  border-radius: 10px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #991b1b;
  font-size: 0.875rem;
  line-height: 1.5;
  text-align: left;

  strong {
    display: block;
    margin-bottom: 0.35rem;
  }
`;

const BtnSecondary = styled.button`
  width: 100%;
  padding: 0.625rem 1rem;
  background: #fff;
  color: ${BRAND.blue};
  border: 1px solid ${BRAND.blue};
  border-radius: 10px;
  font-size: 0.9375rem;
  font-weight: 600;
  cursor: pointer;
  margin-top: 0.75rem;
  &:hover:not(:disabled) {
    background: ${BRAND.lightGray};
  }
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const PrivacyNote = styled.p`
  font-size: 0.75rem;
  color: #94a3b8;
  text-align: center;
  margin: 1rem 0 0;
  line-height: 1.4;
`;

const SkillDerivedBox = styled.div`
  background: linear-gradient(180deg, #f0f9ff 0%, #e0f2fe 100%);
  border: 1px solid #7dd3fc;
  border-radius: 12px;
  padding: 1rem 1.125rem;
  margin-bottom: 1rem;
  font-size: 0.875rem;
  color: #0c4a6e;
  line-height: 1.5;
`;

const SkillDerivedStrong = styled.strong`
  display: block;
  margin-bottom: 0.35rem;
  color: ${BRAND.blue};
`;

const BtnSkillInfo = styled.button`
  width: 100%;
  padding: 0.65rem 1rem;
  margin-bottom: 1rem;
  background: #f8fafc;
  color: ${BRAND.blue};
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.18s ease, border-color 0.18s ease;
  &:hover {
    background: #f1f5f9;
    border-color: ${BRAND.blue};
  }
`;

const WizardPanel = styled.div`
  position: relative;
  min-height: 200px;
`;

const slideSx = {
  initial: { opacity: 0, x: 18 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -14 },
  transition: { duration: 0.28, ease: [0.4, 0, 0.2, 1] },
};

function RegisterPlayerWizardStepper({ wizardStep, totalSteps, labels, onStepClick }) {
  const fillPct =
    totalSteps <= 1 ? 0 : ((Math.min(wizardStep, totalSteps) - 1) / (totalSteps - 1)) * 100;

  return (
    <StepperRow role="navigation" aria-label="Registration steps">
      <StepperTrack aria-hidden />
      <StepperFill
        initial={{ width: '0%' }}
        animate={{ width: `${fillPct}%` }}
        transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
        aria-hidden
      />
      {Array.from({ length: totalSteps }, (_, i) => {
        const step = i + 1;
        const done = wizardStep > step;
        const active = wizardStep === step;
        const clickable = done && typeof onStepClick === 'function';
        const handleClick = () => {
          if (clickable) onStepClick(step);
        };
        return (
          <StepItem
            key={step}
            type="button"
            $clickable={clickable}
            disabled={!clickable}
            aria-current={active ? 'step' : undefined}
            aria-label={`${labels[i] || `Step ${step}`}${done ? ', completed' : active ? ', current' : ''}`}
            onClick={handleClick}
          >
            <StepBubble $active={active} $done={done} aria-hidden>
              {done ? '✓' : step}
            </StepBubble>
            <StepLabel $active={active} $done={done}>
              {labels[i] || `Step ${step}`}
            </StepLabel>
          </StepItem>
        );
      })}
    </StepperRow>
  );
}

export default function RegisterPlayerPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({
    tenantId: '',
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    phoneNumber: '',
    dateOfBirth: '',
    sex: '',
    homeClub: '',
    homeCanton: '',
  });
  const [playtomicBandId, setPlaytomicBandId] = useState('');
  const [swissLevel, setSwissLevel] = useState('');
  const [skillMappingModalOpen, setSkillMappingModalOpen] = useState(false);
  const [exactSkillLevel, setExactSkillLevel] = useState('');
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [agreeRules, setAgreeRules] = useState(false);
  const [partnerCommunications, setPartnerCommunications] = useState(false);

  const [signupPhase, setSignupPhase] = useState('wizard'); // 'wizard' | 'verify'
  const [pendingRegistrationId, setPendingRegistrationId] = useState(null);
  const [maskedEmail, setMaskedEmail] = useState('');
  const [turnstileSiteKey, setTurnstileSiteKey] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  /** idle | loading | ready | blocked */
  const [turnstileUiState, setTurnstileUiState] = useState('idle');
  const [turnstileRenderKey, setTurnstileRenderKey] = useState(0);
  const turnstileContainerRef = useRef(null);
  const turnstileWidgetIdRef = useRef(null);
  const turnstileErrorCountRef = useRef(0);

  const wizardLabels = useMemo(
    () => [
      t('playerRegister.wizardStepAccount'),
      t('playerRegister.wizardStepDetails'),
      t('playerRegister.wizardStepClubSkill'),
      t('playerRegister.wizardStepReview'),
    ],
    [t]
  );

  const { data: signupConfigRes } = useQuery(
    ['playerSignupConfig'],
    () => authAPI.getPlayerSignupConfig(),
    { staleTime: 10 * 60 * 1000 }
  );

  useEffect(() => {
    const k = signupConfigRes?.data?.turnstileSiteKey;
    setTurnstileSiteKey(typeof k === 'string' && k.trim() ? k.trim() : null);
  }, [signupConfigRes]);

  const { data: countriesRes, isLoading: loadingCountries } = useQuery(
    ['tenantsForPlayerSignup'],
    () => authAPI.getTenantsForPlayerSignup(),
    { staleTime: 5 * 60 * 1000 }
  );

  const { data: cantonOptsRes, isLoading: loadingCantons } = useQuery(
    ['playerSignupCantonOptions'],
    () => authAPI.getPlayerSignupCantonOptions(),
    { staleTime: 60 * 60 * 1000 }
  );
  const countries = useMemo(() => {
    const raw = countriesRes?.data;
    return Array.isArray(raw) ? raw : [];
  }, [countriesRes]);
  const cantonOptions = useMemo(() => {
    const raw = cantonOptsRes?.data;
    return Array.isArray(raw) ? raw : [];
  }, [cantonOptsRes]);

  const selectedCountrySignup = useMemo(
    () => countries.find((c) => String(c.id) === String(form.tenantId)),
    [countries, form.tenantId]
  );
  const signupCampaignsOnlyNotice = !!selectedCountrySignup?.campaigns_only_for_new_registrations;

  useEffect(() => {
    if (countries.length === 0) return;
    const ch = countries.find((c) => String(c.countryCode || '').toUpperCase() === 'CH');
    const pick = ch || countries[0];
    const id = String(pick.id);
    setForm((prev) => (prev.tenantId === id ? prev : { ...prev, tenantId: id }));
  }, [countries]);

  const { skillRange } = useMemo(
    () => deriveSkillRangeFromSelections(playtomicBandId, swissLevel),
    [playtomicBandId, swissLevel]
  );

  useEffect(() => {
    setExactSkillLevel('');
  }, [skillRange]);

  const exactSkillOptions = useMemo(() => exactLevelsForRange(skillRange), [skillRange]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const resetTurnstileWidget = () => {
    if (typeof window !== 'undefined' && window.turnstile && turnstileWidgetIdRef.current != null) {
      try {
        window.turnstile.reset(turnstileWidgetIdRef.current);
      } catch (_) {
        /* noop */
      }
    }
    setTurnstileToken('');
  };

  useEffect(() => {
    if (signupPhase !== 'verify') return undefined;
    if (!turnstileSiteKey) return undefined;

    // Wait one frame so TurnstileBox ref is mounted
    let cancelled = false;
    let loadListener = null;
    let scriptEl = null;
    let blockedTimer = null;

    const markBlocked = () => {
      if (cancelled) return;
      setTurnstileUiState('blocked');
      setTurnstileToken('');
    };

    const renderInner = () => {
      if (cancelled) return;
      const container = turnstileContainerRef.current;
      if (typeof window === 'undefined' || !window.turnstile || !container) return;
      if (turnstileWidgetIdRef.current != null) {
        try {
          window.turnstile.remove(turnstileWidgetIdRef.current);
        } catch (_) {
          /* noop */
        }
        turnstileWidgetIdRef.current = null;
      }
      try {
        turnstileWidgetIdRef.current = window.turnstile.render(container, {
          sitekey: turnstileSiteKey,
          callback: (token) => {
            if (cancelled) return;
            turnstileErrorCountRef.current = 0;
            setTurnstileUiState('ready');
            setTurnstileToken(token || '');
          },
          'expired-callback': () => {
            if (cancelled) return;
            setTurnstileToken('');
            setTurnstileUiState('loading');
          },
          'error-callback': () => {
            if (cancelled) return;
            setTurnstileToken('');
            turnstileErrorCountRef.current += 1;
            if (turnstileErrorCountRef.current >= 2) markBlocked();
            else setTurnstileUiState('loading');
          },
          'timeout-callback': () => {
            if (cancelled) return;
            setTurnstileToken('');
            turnstileErrorCountRef.current += 1;
            if (turnstileErrorCountRef.current >= 2) markBlocked();
          },
        });
        setTurnstileUiState((s) => (s === 'blocked' ? 'blocked' : 'loading'));
      } catch (err) {
        console.warn('[turnstile] render failed', err);
        markBlocked();
      }
    };

    turnstileErrorCountRef.current = 0;
    setTurnstileUiState('loading');
    setTurnstileToken('');

    const raf = requestAnimationFrame(() => {
      if (cancelled) return;
      scriptEl = document.querySelector('script[data-turnstile-loader="hps-register"]');
      if (!scriptEl) {
        scriptEl = document.createElement('script');
        scriptEl.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
        scriptEl.async = true;
        scriptEl.defer = true;
        scriptEl.dataset.turnstileLoader = 'hps-register';
        scriptEl.onload = () => renderInner();
        scriptEl.onerror = () => {
          console.warn('[turnstile] script blocked or failed to load');
          markBlocked();
        };
        document.body.appendChild(scriptEl);
      } else if (window.turnstile) {
        renderInner();
      } else {
        loadListener = () => renderInner();
        scriptEl.addEventListener('load', loadListener);
        blockedTimer = setTimeout(() => {
          if (!window.turnstile) markBlocked();
        }, 4000);
      }
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      if (blockedTimer) clearTimeout(blockedTimer);
      if (scriptEl && loadListener) scriptEl.removeEventListener('load', loadListener);
      if (typeof window !== 'undefined' && window.turnstile && turnstileWidgetIdRef.current != null) {
        try {
          window.turnstile.remove(turnstileWidgetIdRef.current);
        } catch (_) {
          /* noop */
        }
        turnstileWidgetIdRef.current = null;
      }
    };
  }, [signupPhase, turnstileSiteKey, turnstileRenderKey]);

  const retryTurnstile = () => {
    turnstileErrorCountRef.current = 0;
    setTurnstileUiState('loading');
    setTurnstileToken('');
    setTurnstileRenderKey((k) => k + 1);
  };

  const validateStep1 = () => {
    if (!form.email?.trim()) {
      toast.error(t('playerRegister.errorEmail'));
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      toast.error(t('playerRegister.errorEmailInvalid'));
      return false;
    }
    if ((form.password || '').length < 6) {
      toast.error(t('playerRegister.errorPasswordMin'));
      return false;
    }
    if (form.password !== form.confirmPassword) {
      toast.error(t('playerRegister.errorPasswordMatch'));
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!form.tenantId) {
      toast.error(t('playerRegister.errorRegion'));
      return false;
    }
    if (!form.firstName?.trim()) {
      toast.error(t('playerRegister.errorFirstName'));
      return false;
    }
    if (!form.lastName?.trim()) {
      toast.error(t('playerRegister.errorLastName'));
      return false;
    }
    if (!form.phoneNumber?.trim()) {
      toast.error(t('playerRegister.errorPhone'));
      return false;
    }
    return true;
  };

  const validateStep3 = () => {
    if (!form.homeCanton || !String(form.homeCanton).trim()) {
      toast.error(t('playerRegister.errorCanton'));
      return false;
    }
    const hasPlaytomic = !!(playtomicBandId && String(playtomicBandId).trim());
    const hasSwiss = !!(swissLevel && String(swissLevel).trim());
    if (!hasPlaytomic && !hasSwiss) {
      toast.error(t('playerRegister.errorSkillReferenceRequired'));
      return false;
    }
    if (!skillRange) {
      toast.error(t('playerRegister.errorSkillBand'));
      return false;
    }
    if (!exactSkillLevel) {
      toast.error(t('playerRegister.errorExactSkill'));
      return false;
    }
    return true;
  };

  const goNext = () => {
    if (wizardStep === 1) {
      if (!validateStep1()) return;
      setWizardStep(2);
      return;
    }
    if (wizardStep === 2) {
      if (!validateStep2()) return;
      setWizardStep(3);
      return;
    }
    if (wizardStep === 3) {
      if (!validateStep3()) return;
      setWizardStep(4);
    }
  };

  const goBack = () => setWizardStep((s) => Math.max(1, s - 1));

  const goToWizardStep = (targetStep) => {
    const step = Number(targetStep);
    if (!Number.isFinite(step) || step < 1 || step >= wizardStep) return;
    setWizardStep(step);
  };

  const sendVerificationRequest = async () => {
    if (!agreeTerms) {
      toast.error(t('register.agreeTermsRequired'));
      return;
    }
    if (!agreeRules) {
      toast.error(t('register.agreeRulesRequired'));
      return;
    }
    setSubmitting(true);
    try {
      const res = await authAPI.registerPlayerStart({
        tenantId: form.tenantId,
        email: form.email.trim(),
        password: form.password,
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        phoneNumber: form.phoneNumber.trim(),
        dateOfBirth: '',
        sex: '',
        homeClub: '',
        skillLevel: parseFloat(exactSkillLevel),
        instagramHandle: null,
        homeCanton: form.homeCanton.trim(),
        agreeTerms: true,
        agreeRules: true,
        partnerCommunications: !!partnerCommunications,
      });
      if (res?.success && res?.data?.pendingRegistrationId) {
        setPendingRegistrationId(res.data.pendingRegistrationId);
        setMaskedEmail(res.data.maskedEmail || '');
        setSignupPhase('verify');
        setVerificationCode('');
        setTurnstileToken('');
        toast.success(res.message || 'Check your email for a verification code.');
      } else {
        toast.error(res?.message || 'Could not send verification email');
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Registration failed';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (!pendingRegistrationId) return;
    setSubmitting(true);
    try {
      const res = await authAPI.registerPlayerResendCode({
        pendingRegistrationId,
      });
      if (res?.success) {
        toast.success(res.message || 'New code sent.');
        if (res?.data?.maskedEmail) setMaskedEmail(res.data.maskedEmail);
        resetTurnstileWidget();
      } else {
        toast.error(res?.message || 'Resend failed');
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Resend failed';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerifyComplete = async (e) => {
    e.preventDefault();
    const codeNorm = verificationCode.trim().replace(/\s/g, '');
    if (!/^\d{6}$/.test(codeNorm)) {
      toast.error('Enter the 6-digit code from your email.');
      return;
    }
    if (turnstileSiteKey && !turnstileToken) {
      toast.error(
        turnstileUiState === 'blocked'
          ? t('playerRegister.turnstileBlockedBody')
          : t('playerRegister.turnstileRequiredToast')
      );
      return;
    }
    setSubmitting(true);
    try {
      const leadEventId =
        (typeof crypto !== 'undefined' && crypto.randomUUID && crypto.randomUUID()) ||
        `lead_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
      const res = await authAPI.registerPlayerComplete({
        pendingRegistrationId,
        code: codeNorm,
        turnstileToken: turnstileSiteKey ? turnstileToken : '',
        attribution: getAttributionForSubmission(),
        metaEventId: leadEventId,
      });
      if (res?.success) {
        trackEvent(
          'Lead',
          {
            content_name: 'Player Account Signup',
            content_category: 'Registration',
            value: 1,
            currency: 'CHF',
          },
          leadEventId
        );
        toast.success(res.message || 'Account created. You can now log in with your email and password.');
        navigate('/login?registered=1');
      } else {
        toast.error(res?.message || 'Verification failed');
        resetTurnstileWidget();
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Verification failed';
      toast.error(msg);
      resetTurnstileWidget();
    } finally {
      setSubmitting(false);
    }
  };

  const cantonLabel = useMemo(() => {
    const c = cantonOptions.find((x) => String(x.code) === String(form.homeCanton));
    if (!c) return form.homeCanton || '—';
    return cantonOptionLabel(c);
  }, [cantonOptions, form.homeCanton]);

  const skillSummary = useMemo(() => {
    if (!exactSkillLevel || !skillRange) return '—';
    const bandLabel = skillRangeHpsLabel(skillRange);
    const parts = [];
    if (playtomicBandId) {
      const row = PLAYTOMIC_BAND_OPTIONS.find((o) => o.id === playtomicBandId);
      if (row) parts.push(t(row.labelKey));
    }
    if (swissLevel && String(swissLevel).trim()) {
      const n = Number(swissLevel);
      const sRow = SWISS_PADEL_LEVEL_OPTIONS.find((o) => o.level === n);
      if (sRow) parts.push(t(sRow.labelKey));
    }
    const refLine = parts.length ? parts.join(' · ') : '';
    return refLine
      ? `${exactSkillLevel} (${bandLabel}) — ${refLine}`
      : `${exactSkillLevel} (${bandLabel})`;
  }, [skillRange, exactSkillLevel, playtomicBandId, swissLevel, t]);

  const step2Blocked = submitting || loadingCountries || countries.length === 0 || !form.tenantId;

  const step34Blocked =
    step2Blocked || loadingCantons || cantonOptions.length === 0;

  return (
    <>
      <PageWrap>
        <Card>
          <BrandRow>
            <img src="/images/logo/logo.png" alt="Helvetic Padel Series" loading="eager" />
          </BrandRow>

          {signupPhase === 'verify' ? (
            <>
              <HeroTitle>{t('playerRegister.verifyTitle')}</HeroTitle>
              <HeroSubtitle>{t('playerRegister.verifySubtitle')}</HeroSubtitle>
              <form onSubmit={handleVerifyComplete}>
                <Label>{t('playerRegister.verificationCodeLabel')}</Label>
                <InputAdornWrap style={{ paddingLeft: '0.75rem' }}>
                  <FaEnvelope aria-hidden />
                  <input
                    name="verificationCode"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={verificationCode}
                    onChange={(ev) => setVerificationCode(ev.target.value.replace(/[^\d\s]/g, ''))}
                    placeholder={t('playerRegister.verificationCodePlaceholder')}
                    maxLength={8}
                  />
                </InputAdornWrap>
                <p style={{ fontSize: '0.8125rem', color: '#4b5563', marginBottom: '1rem' }}>
                  {t('playerRegister.sentTo')}{' '}
                  {maskedEmail ? <strong>{maskedEmail}</strong> : t('playerRegister.yourEmail')}
                </p>
                {turnstileSiteKey ? (
                  <>
                    <Label style={{ marginTop: '0.5rem' }}>{t('playerRegister.securityCheck')}</Label>
                    {turnstileUiState === 'blocked' ? (
                      <TurnstileAlert>
                        <strong>{t('playerRegister.turnstileBlockedTitle')}</strong>
                        {t('playerRegister.turnstileBlockedBody')}
                        <BtnSecondary
                          type="button"
                          style={{ marginTop: '0.75rem' }}
                          onClick={retryTurnstile}
                        >
                          {t('playerRegister.turnstileRetry')}
                        </BtnSecondary>
                      </TurnstileAlert>
                    ) : (
                      <>
                        <TurnstileBox ref={turnstileContainerRef} />
                        <Hint>{t('playerRegister.turnstileHint')}</Hint>
                      </>
                    )}
                  </>
                ) : (
                  <Hint>{t('playerRegister.noCaptchaDev')}</Hint>
                )}
                <BtnPrimary
                  type="submit"
                  disabled={
                    submitting ||
                    !/^\d{6}$/.test(verificationCode.trim().replace(/\s/g, '')) ||
                    (!!turnstileSiteKey && !turnstileToken)
                  }
                >
                  {submitting ? t('playerRegister.creatingAccount') : t('playerRegister.createAccount')}
                </BtnPrimary>
                <BtnSecondary type="button" disabled={submitting || !pendingRegistrationId} onClick={handleResend}>
                  {t('playerRegister.resendCode')}
                </BtnSecondary>
              </form>
            </>
          ) : (
            <>
              <HeroTitle>{t('playerRegister.heroTitle')}</HeroTitle>
              <HeroSubtitle>{t('playerRegister.heroSubtitle')}</HeroSubtitle>

              <RegisterPlayerWizardStepper
                wizardStep={wizardStep}
                totalSteps={4}
                labels={wizardLabels}
                onStepClick={goToWizardStep}
              />

              <WizardPanel>
                <AnimatePresence mode="wait">
                  {wizardStep === 1 && (
                    <motion.div key="w1" {...slideSx}>
                      <StepTitle>{t('playerRegister.step1Title')}</StepTitle>
                      <StepLead>{t('playerRegister.step1Lead')}</StepLead>

                      <Label htmlFor="reg-email">{t('playerRegister.emailLabel')}</Label>
                      <InputAdornWrap>
                        <FaEnvelope aria-hidden />
                        <input
                          id="reg-email"
                          name="email"
                          type="email"
                          value={form.email}
                          onChange={handleChange}
                          autoComplete="email"
                          placeholder={t('playerRegister.emailPlaceholder')}
                        />
                      </InputAdornWrap>

                      <Label htmlFor="reg-password">{t('playerRegister.passwordLabel')}</Label>
                      <InputAdornWrap>
                        <FaLock aria-hidden />
                        <input
                          id="reg-password"
                          name="password"
                          type={showPassword ? 'text' : 'password'}
                          value={form.password}
                          onChange={handleChange}
                          autoComplete="new-password"
                          placeholder={t('playerRegister.passwordPlaceholder')}
                        />
                        <TogglePw type="button" tabIndex={-1} onClick={() => setShowPassword((v) => !v)}>
                          {showPassword ? t('playerRegister.hidePassword') : t('playerRegister.showPassword')}
                        </TogglePw>
                      </InputAdornWrap>

                      <Label htmlFor="reg-confirm">{t('playerRegister.confirmPasswordLabel')}</Label>
                      <InputAdornWrap>
                        <FaLock aria-hidden />
                        <input
                          id="reg-confirm"
                          name="confirmPassword"
                          type={showPassword ? 'text' : 'password'}
                          value={form.confirmPassword}
                          onChange={handleChange}
                          autoComplete="new-password"
                          placeholder={t('playerRegister.confirmPasswordPlaceholder')}
                        />
                      </InputAdornWrap>

                      <BtnRow>
                        <BtnPrimary type="button" style={{ flex: 1 }} onClick={goNext}>
                          {t('playerRegister.continue')}
                        </BtnPrimary>
                      </BtnRow>
                    </motion.div>
                  )}

                  {wizardStep === 2 && (
                    <motion.div key="w2" {...slideSx}>
                      <StepTitle>{t('playerRegister.step2Title')}</StepTitle>
                      <StepLead>{t('playerRegister.step2Lead')}</StepLead>

                      <Row>
                        <FieldWrap>
                          <Label htmlFor="fn">{t('playerRegister.firstName')}</Label>
                          <InnerInput
                            id="fn"
                            name="firstName"
                            value={form.firstName}
                            onChange={handleChange}
                            autoComplete="given-name"
                            placeholder={t('playerRegister.firstName')}
                          />
                        </FieldWrap>
                        <FieldWrap>
                          <Label htmlFor="ln">{t('playerRegister.lastName')}</Label>
                          <InnerInput
                            id="ln"
                            name="lastName"
                            value={form.lastName}
                            onChange={handleChange}
                            autoComplete="family-name"
                            placeholder={t('playerRegister.lastName')}
                          />
                        </FieldWrap>
                      </Row>

                      <Label htmlFor="ph">{t('playerRegister.phoneLabel')}</Label>
                      <InputAdornWrap style={{ marginBottom: '1rem' }}>
                        <MdPhoneAndroid aria-hidden />
                        <input
                          id="ph"
                          name="phoneNumber"
                          type="tel"
                          value={form.phoneNumber}
                          onChange={handleChange}
                          required
                          placeholder={t('playerRegister.phonePlaceholder')}
                        />
                      </InputAdornWrap>

                      {signupCampaignsOnlyNotice ? (
                        <Hint style={{ color: '#0f766e', lineHeight: 1.5, marginBottom: '1rem' }}>
                          {t('playerRegister.campaignFirstNotice')}
                        </Hint>
                      ) : null}

                      {submitting || loadingCountries ? (
                        <Hint style={{ color: '#0369a1' }}>{t('playerRegister.loadingRegions')}</Hint>
                      ) : null}
                      {!loadingCountries &&
                      step2Blocked &&
                      (countries.length === 0 || !form.tenantId) ? (
                        <Hint style={{ color: '#b45309', lineHeight: 1.45 }}>
                          {t('playerRegister.loadRegionsError')}
                        </Hint>
                      ) : null}

                      <BtnRow>
                        <BtnOutline type="button" onClick={goBack}>
                          {t('playerRegister.back')}
                        </BtnOutline>
                        <BtnPrimary type="button" disabled={step2Blocked} onClick={goNext}>
                          {t('playerRegister.continue')}
                        </BtnPrimary>
                      </BtnRow>
                    </motion.div>
                  )}

                  {wizardStep === 3 && (
                    <motion.div key="w3" {...slideSx}>
                      <SectionHeading>{t('playerRegister.step3ClubSectionTitle')}</SectionHeading>
                      <SectionLead>{t('playerRegister.step3ClubSectionLead')}</SectionLead>

                      <Label>{t('playerRegister.homeCantonLabel')}</Label>
                      <Select
                        name="homeCanton"
                        value={form.homeCanton}
                        onChange={handleChange}
                        required
                        disabled={loadingCantons || cantonOptions.length === 0}
                      >
                        <option value="">{t('playerRegister.selectCanton')}</option>
                        {cantonOptions.map((c) => {
                          const code = c.code != null ? String(c.code) : '';
                          if (!code) return null;
                          return (
                            <option key={code} value={code}>
                              {cantonOptionLabel(c)}
                            </option>
                          );
                        })}
                      </Select>

                      <SectionHeading style={{ marginTop: '1.5rem' }}>
                        {t('playerRegister.step3SkillSectionTitle')}
                      </SectionHeading>
                      <Hint style={{ marginBottom: '0.75rem' }}>
                        {t('phase2.playerProfile.hints.skillOncePlaytomicSwiss')}
                      </Hint>

                      <Label>{t('playerRegister.playtomicBandLabel')}</Label>
                      <Select
                        value={playtomicBandId}
                        onChange={(e) => {
                          const v = e.target.value;
                          setPlaytomicBandId(v);
                          if (v) setSwissLevel('');
                        }}
                      >
                        <option value="">{t('playerRegister.playtomicBandPlaceholder')}</option>
                        {PLAYTOMIC_BAND_OPTIONS.map((o) => (
                          <option key={o.id} value={o.id}>
                            {t(o.labelKey)}
                          </option>
                        ))}
                      </Select>

                      <Label>{t('playerRegister.swissPadelLevelLabel')}</Label>
                      <Select
                        value={swissLevel}
                        onChange={(e) => {
                          const v = e.target.value;
                          setSwissLevel(v);
                          if (v) setPlaytomicBandId('');
                        }}
                      >
                        <option value="">{t('playerRegister.swissPadelLevelPlaceholder')}</option>
                        {SWISS_PADEL_LEVEL_OPTIONS.map((o) => (
                          <option key={o.level} value={String(o.level)}>
                            {t(o.labelKey)}
                          </option>
                        ))}
                      </Select>
                      <Hint>{t('playerRegister.skillReferenceHint')}</Hint>

                      <BtnSkillInfo type="button" onClick={() => setSkillMappingModalOpen(true)}>
                        {t('playerRegister.skillMappingMoreInfo')}
                      </BtnSkillInfo>

                      {skillRange ? (
                        <SkillDerivedBox>
                          <SkillDerivedStrong>{t('playerRegister.skillDerivedTitle')}</SkillDerivedStrong>
                          {t('playerRegister.skillLockNotice')}{' '}
                          <strong>{skillRangeHpsLabel(skillRange)}</strong>
                        </SkillDerivedBox>
                      ) : null}

                      {skillRange ? (
                        <>
                          <Label>{t('playerRegister.exactHpsLevelLabel')}</Label>
                          <Select
                            value={exactSkillLevel}
                            onChange={(e) => setExactSkillLevel(e.target.value)}
                            required
                          >
                            <option value="">{t('playerRegister.exactHpsLevelPlaceholder')}</option>
                            {exactSkillOptions.map((v) => (
                              <option key={v} value={v}>
                                {v}
                              </option>
                            ))}
                          </Select>
                        </>
                      ) : null}

                      {submitting || loadingCountries || loadingCantons ? (
                        <Hint style={{ color: '#0369a1' }}>{t('playerRegister.loadingRegionsCantons')}</Hint>
                      ) : null}
                      {!loadingCountries &&
                      !loadingCantons &&
                      step34Blocked &&
                      (cantonOptions.length === 0 || countries.length === 0 || !form.tenantId) ? (
                        <Hint style={{ color: '#b45309', lineHeight: 1.45 }}>
                          {t('playerRegister.loadRegionsError')}
                        </Hint>
                      ) : null}

                      <BtnRow>
                        <BtnOutline type="button" onClick={goBack}>
                          {t('playerRegister.back')}
                        </BtnOutline>
                        <BtnPrimary type="button" disabled={step34Blocked} onClick={goNext}>
                          {t('playerRegister.continue')}
                        </BtnPrimary>
                      </BtnRow>
                    </motion.div>
                  )}

                  {wizardStep === 4 && (
                    <motion.div key="w4" {...slideSx}>
                      <StepTitle>{t('playerRegister.step4Title')}</StepTitle>
                      <StepLead>{t('playerRegister.step4Lead')}</StepLead>

                      <SummaryBlock>
                        <SummaryHeadRow>
                          <SummaryHeading style={{ marginBottom: 0 }}>{t('playerRegister.summaryAccount')}</SummaryHeading>
                          <SummaryEdit type="button" onClick={() => setWizardStep(1)}>
                            {t('playerRegister.edit')}
                          </SummaryEdit>
                        </SummaryHeadRow>
                        <SummaryBody>{form.email?.trim() || '—'}</SummaryBody>
                      </SummaryBlock>

                      <SummaryBlock>
                        <SummaryHeadRow>
                          <SummaryHeading style={{ marginBottom: 0 }}>{t('playerRegister.summaryPersonal')}</SummaryHeading>
                          <SummaryEdit type="button" onClick={() => setWizardStep(2)}>
                            {t('playerRegister.edit')}
                          </SummaryEdit>
                        </SummaryHeadRow>
                        <SummaryBody>
                          {[form.firstName, form.lastName].filter(Boolean).join(' ') || '—'}
                          <br />
                          {form.phoneNumber?.trim() ? form.phoneNumber.trim() : t('playerRegister.summaryNoPhone')}
                        </SummaryBody>
                      </SummaryBlock>

                      <SummaryBlock>
                        <SummaryHeadRow>
                          <SummaryHeading style={{ marginBottom: 0 }}>{t('playerRegister.summaryClubSkill')}</SummaryHeading>
                          <SummaryEdit type="button" onClick={() => setWizardStep(3)}>
                            {t('playerRegister.edit')}
                          </SummaryEdit>
                        </SummaryHeadRow>
                        <SummaryBody>
                          {t('playerRegister.summaryCanton')}: {cantonLabel || '—'}
                          <br />
                          {t('playerRegister.summarySkill')}: {skillSummary}
                          <br />
                          <span style={{ color: '#6b7280', fontSize: '0.8125rem' }}>
                            {t('playerRegister.profileCompleteAfterLogin')}
                          </span>
                        </SummaryBody>
                      </SummaryBlock>

                      <PlayerRegistrationConsentSection
                        agreeTerms={agreeTerms}
                        onAgreeTermsChange={setAgreeTerms}
                        agreeRules={agreeRules}
                        onAgreeRulesChange={setAgreeRules}
                        partnerCommunications={partnerCommunications}
                        onPartnerCommunicationsChange={setPartnerCommunications}
                      />

                      <PrivacyNote>{t('playerRegister.privacyNote')}</PrivacyNote>

                      {submitting || loadingCountries || loadingCantons ? (
                        <Hint style={{ color: '#0369a1' }}>{t('playerRegister.loadingRegionsCantons')}</Hint>
                      ) : null}
                      {!loadingCountries &&
                      !loadingCantons &&
                      step34Blocked &&
                      (cantonOptions.length === 0 || countries.length === 0 || !form.tenantId) ? (
                        <Hint style={{ color: '#b45309', lineHeight: 1.45 }}>
                          {t('playerRegister.loadRegionsError')}
                        </Hint>
                      ) : null}

                      <BtnRow>
                        <BtnOutline type="button" onClick={goBack} disabled={submitting}>
                          {t('playerRegister.back')}
                        </BtnOutline>
                        <BtnPrimary
                          type="button"
                          disabled={step34Blocked || submitting}
                          onClick={sendVerificationRequest}
                        >
                          {submitting ? t('playerRegister.sending') : (
                            <>
                              <FaEnvelope aria-hidden /> {t('playerRegister.sendVerification')}
                            </>
                          )}
                        </BtnPrimary>
                      </BtnRow>
                    </motion.div>
                  )}
                </AnimatePresence>
              </WizardPanel>
            </>
          )}

          <LoginLink>
            {t('playerRegister.alreadyHaveAccount')}{' '}
            <Link to="/login">{t('playerRegister.logIn')}</Link>
          </LoginLink>
        </Card>
      </PageWrap>

      <SkillReferenceModal open={skillMappingModalOpen} onClose={() => setSkillMappingModalOpen(false)} />
    </>
  );
}
